import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Select, Alert, Button, Space, Divider, Table, Tag, Input, DatePicker, Tooltip } from 'antd';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import type { CreateSlotDTO } from '../../../Modules/Slot/Model/Slot';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { requiresTomorrowStartDate } from '../../../Utils/limitedPartnerAdmin';

const { Paragraph, Text } = Typography;
const { Option } = Select;

interface Props {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSubmit: (request: { count: number; slots: CreateSlotDTO[] }) => Promise<void> | void;
}

type RawRow = Record<string, any>;

type Field = keyof Omit<CreateSlotDTO, 'sellerId'> | 'quantity';

const headerMap: Record<Field, string[]> = {
  slot_number: ['slot_number', '슬롯번호', '슬롯 번호', '슬롯'],
  main_keyword: ['main_keyword', '메인키워드', '메인 키워드'],
  word_keyword: [],
  mid_single: ['mid_single', '단일MID', '단일 MID'],
  mid_compare: ['mid_compare', '가격비교MID', '가격비교 MID', '원부MID', '원부 MID'],
  product_url: ['product_url', '상품URL', '상품 URL'],
  compare_url: ['compare_url', '가격비교URL', '가격비교 URL', '가격비교', '가격 비교', '가격비교링크'],
  memo: ['memo', '메모'],
  mb_id: ['mb_id', 'mbId', '회원ID', '회원 ID'],
  start_date: ['start_date', '시작일', 'start', 'Start'],
  end_date: ['end_date', '종료일', 'end', 'End'],
  status: ['status', '상태'],
  manager: ['manager', '매니저', '담당'],
  quantity: ['quantity', '수량', '개수', '갯수', '수량(개)', '수량(개수)'],
};

const SELLER_HEADER = '셀러(Id)';



const ExcelImportModal: React.FC<Props> = ({ open, file, onClose, onSubmit }) => {
  const me = useAppSelector(selectUser);
  const allowTodayStart = useMemo(() => !requiresTomorrowStartDate(me), [me]);
  const accounts = useAppSelector(selectAccounts);
  const sellers = useMemo(
    () => accounts.filter(a => a.grade === 'SELLER').sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  
  const [parseError, setParseError] = useState<string | null>(null);
  type RowDraft = Partial<CreateSlotDTO> & { key: number };
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [bulkSellerId, setBulkSellerId] = useState<number | undefined>(undefined);
  const [mapping, setMapping] = useState<Record<Field, string | null>>({
    slot_number: null,
    main_keyword: null,
    word_keyword: null,
    mid_single: null,
    mid_compare: null,
    product_url: null,
    compare_url: null,
    memo: null,
    mb_id: null,
    start_date: null,
    end_date: null,
    status: null,
    manager: null,
    quantity: null,
  });

  // 반응형: 모바일 여부
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open || !file) {
      setIsParsing(false);
      return;
    }
    setParseError(null);
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: '' });
        setRawRows(rows);
      } catch (err) {
        setParseError('엑셀 파일을 읽는 중 오류가 발생했습니다.');
        setIsParsing(false);
      } finally {
      }
    };
    reader.onerror = () => {
      setParseError('엑셀 파일을 읽을 수 없습니다.');
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  }, [open, file]);

  const detectedHeaders = useMemo(() => {
    const set = new Set<string>();
    rawRows.forEach(r => Object.keys(r).forEach(k => set.add(String(k))));
    return Array.from(set);
  }, [rawRows]);

  const resolveSellerId = useCallback((value: any): number | undefined => {
    if (value == null || value === '') return undefined;

    const raw = String(value).trim();
    if (!raw) return undefined;

    // 1) 정확 일치 (username) - 최우선 (숫자로만 이루어진 경우 포함)
    const usernameCandidate = raw.split('(')[0].trim().split(/\s+/)[0].trim();
    const lower = usernameCandidate.toLowerCase();
    const matched = sellers.find(s => s.username.toLowerCase() === lower);
    if (matched) return matched.id;

    // 2) 숫자인 경우 ID로 검색
    if (/^\d+$/.test(raw)) return Number(raw);

    return undefined;
  }, [sellers]);

  useEffect(() => {
    if (detectedHeaders.length === 0) return;
    const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
    const next: Record<Field, string | null> = { ...mapping } as any;
    let changed = false;
    (Object.keys(headerMap) as Field[]).forEach(field => {
      if (next[field]) return;
      const aliases = headerMap[field].map(norm);
      const found = detectedHeaders.find(h => aliases.includes(norm(h)) || aliases.some(a => norm(h).includes(a)));
      if (found) {
        next[field] = found;
        changed = true;
      }
    });
    if (changed) setMapping(next);
  }, [detectedHeaders, mapping]);

  // 엑셀 시리얼/문자 날짜를 YYYY-MM-DD 로 정규화
  const toDateString = (value: any): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
      const date = new Date(Math.round((value - 25569) * 86400 * 1000));
      return dayjs(date).format('YYYY-MM-DD');
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value);
  };

  // 초기 rows 채우기 (자동 매핑만 사용, UI는 테이블에서 직접 수정)
  useEffect(() => {
    if (rawRows.length === 0) {
      if (isParsing) setIsParsing(false);
      return;
    }

    const generateRows = async () => {
      // 이미 파싱 중이라면 중복 실행 방지
      // (단, FileReader에서 넘겨받은 직후의 첫 실행은 허용해야 함)
      // 여기서는 state를 보고 판단하기보다 로직 흐름으로 제어
      
      const norm = (s: any) => String(s ?? '').trim();
      const initial: RowDraft[] = [];
      let globalIdx = 0;

      rawRows.forEach((r, idx) => {
        const pick = (field: Field): any => {
          const header = mapping[field];
          if (!header) return '';
          return r[header];
        };

        const quantityRaw = pick('quantity');
        const quantity = Math.max(1, parseInt(String(quantityRaw)) || 1);

        const sellerRaw = r[SELLER_HEADER] ?? r['셀러(id)'];
        const startRaw = pick('start_date');
        const endRaw = pick('end_date');

        const baseRow = {
          sellerId: resolveSellerId(sellerRaw),
          manager: (pick('manager') as any) ?? null,
          slot_number: norm(pick('slot_number')),
          main_keyword: norm(pick('main_keyword')),
          word_keyword: '-',
          mid_single: norm(pick('mid_single')),
          mid_compare: norm(pick('mid_compare')),
          product_url: norm(pick('product_url')),
          compare_url: norm(pick('compare_url')),
          memo: norm(pick('memo')) || undefined,
          start_date: toDateString(startRaw),
          end_date: toDateString(endRaw),
          status: (norm(pick('status')) as CreateSlotDTO['status']) || '정상',
        };

        for (let i = 0; i < quantity; i++) {
          initial.push({
            ...baseRow,
            key: globalIdx++,
          });
        }
      });
      setRows(initial);
      setIsParsing(false);
    };

    generateRows();
  }, [rawRows, mapping, resolveSellerId]);

  const minDate = useMemo(() => {
    if (allowTodayStart) return dayjs().startOf('day');
    return dayjs().add(1, 'day').startOf('day');
  }, [allowTodayStart]);

  const getRowErrors = useCallback((r: RowDraft): string[] => {
    const errs: string[] = [];
    if (!r.sellerId) errs.push('셀러 미선택');
    if (!r.start_date) errs.push('시작일 없음');
    else if (dayjs(r.start_date).startOf('day').isBefore(minDate)) {
      errs.push(allowTodayStart ? '시작일은 오늘 이후' : '시작일은 내일 이후');
    }
    if (!r.end_date) errs.push('종료일 없음');
    else if (dayjs(r.end_date).startOf('day').isBefore(minDate)) {
      errs.push(allowTodayStart ? '종료일은 오늘 이후' : '종료일은 내일 이후');
    }
    if (r.start_date && r.end_date) {
      const s = dayjs(r.start_date).startOf('day');
      const e = dayjs(r.end_date).startOf('day');
      if (e.isBefore(s)) errs.push('종료일은 시작일과 같거나 이후여야 합니다');
    }
    if (r.product_url && !isValidUrl(r.product_url)) errs.push('상품 URL 형식이 올바르지 않습니다');
    if (r.compare_url && !isValidUrl(r.compare_url)) errs.push('가격비교 URL 형식이 올바르지 않습니다');
    return errs;
  }, [minDate, allowTodayStart]);

  const isRowValid = useCallback((r: RowDraft) => getRowErrors(r).length === 0, [getRowErrors]);

  const isValidUrl = (url: string) => {
    if (!url) return true; // 비어있는 경우 필수값이 아니면 통과 (필수 여부에 따라 조절)
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  const { mapped, invalidCount } = useMemo(() => {
    const valid: CreateSlotDTO[] = [];
    rows.forEach((r: RowDraft) => {
      if (!isRowValid(r)) return;
      valid.push({
        sellerId: r.sellerId!,
        manager: r.manager ?? null,
        slot_number: r.slot_number || '',
        main_keyword: r.main_keyword || '',
        word_keyword: '-',
        mid_single: r.mid_single || '',
        mid_compare: r.mid_compare || '',
        product_url: r.product_url || '',
        compare_url: r.compare_url || '',
        memo: r.memo,
        start_date: r.start_date!,
        end_date: r.end_date!,
        status: r.status || '정상',
      });
    });
    return { mapped: valid, invalidCount: rows.length - valid.length };
  }, [rows, isRowValid]);

  const canSubmit = useMemo(() => rows.length > 0 && rows.every(isRowValid), [rows, isRowValid]);

  const updateRow = useCallback((key: number, patch: Partial<RowDraft>) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));
  }, []);

  const applyBulkSeller = useCallback((mode: 'all' | 'emptyOnly') => {
    if (!bulkSellerId) return;
    setRows(prev =>
      prev.map(r => {
        if (mode === 'emptyOnly' && r.sellerId) return r;
        return { ...r, sellerId: bulkSellerId };
      })
    );
  }, [bulkSellerId]);

  const Metrics = styled(Space)`
    width: 100%;
  `;

  const FullWidth = styled(Space)`
    width: 100%;
  `;

  return (
    <Modal
      title="엑셀 업로드 - 행별 편집 및 등록"
      open={open}
      onCancel={onClose}
      width={isMobile ? undefined : 1100}
      bodyStyle={isMobile ? { paddingBottom: 0 } : { paddingBottom: 0, maxHeight: '80vh', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onClose}>취소</Button>,
        <Button key="submit" type="primary" disabled={!canSubmit} onClick={() => {
          const slots: CreateSlotDTO[] = mapped;
          onSubmit({ count: 1, slots });
        }}>등록</Button>,
      ]}
    >
      <FullWidth direction="vertical" size="middle">
        <Paragraph>엑셀을 읽었습니다. 각 행에서 셀러 및 값을 직접 수정하세요. 상태는 수정할 수 없습니다.</Paragraph>

        {parseError && <Alert type="error" message={parseError} />}

        <Metrics size="small">
          <Space size="small"><Text>총 행 수:</Text><Text strong>{rows.length}</Text></Space>
          <Space size="small"><Text>등록 가능한:</Text><Text strong>{mapped.length}</Text></Space>
          {invalidCount > 0 && (
            <Space size="small"><Text>등록 불가:</Text><Text type="danger">{invalidCount}</Text></Space>
          )}
        </Metrics>

        <Divider style={{ margin: '8px 0' }} />

        <Space wrap size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap size="small">
            <Text strong>셀러 일괄 적용</Text>
            <Select
              value={bulkSellerId}
              style={{ width: 240 }}
              placeholder="셀러 선택"
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              onChange={(val: number) => setBulkSellerId(val)}
              allowClear
            >
              {sellers.map(s => (
                <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                  {s.username} ({s.name})
                </Option>
              ))}
            </Select>
            <Button
              onClick={() => applyBulkSeller('all')}
              disabled={!bulkSellerId || rows.length === 0}
            >
              전체 행에 적용
            </Button>
            <Button
              onClick={() => applyBulkSeller('emptyOnly')}
              disabled={!bulkSellerId || rows.length === 0}
            >
              미선택 행에만 적용
            </Button>
          </Space>
          <Text type="secondary">행별로 선택된 셀러는 아래 테이블에서 언제든 수정할 수 있습니다.</Text>
        </Space>

        <Table
          size="small"
          pagination={false}
          dataSource={rows}
          scroll={{ x: 1800, y: 500 }}
          loading={isParsing}
          virtual
          columns={[
            {
              title: '셀러',
              dataIndex: 'sellerId',
              width: 220,
              render: (_: unknown, r: RowDraft) => (
                <Tooltip title={(!r.sellerId) ? '셀러를 선택하세요.' : ''}>
                  <Select
                    value={r.sellerId as number | undefined}
                    style={{ width: '100%' }}
                    placeholder="셀러 선택"
                    showSearch
                    optionFilterProp="label"
                    optionLabelProp="label"
                    status={!r.sellerId ? 'error' : undefined}
                    onChange={(val: number) => updateRow(r.key, { sellerId: val })}
                  >
                    {sellers.map(s => (
                      <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                        {s.username} ({s.name})
                      </Option>
                    ))}
                  </Select>
                </Tooltip>
              )
            },
            { title: '메인 키워드', dataIndex: 'main_keyword', width: 200, render: (v: any, r: any) => (
              <Input value={v as string} onChange={e => updateRow(r.key, { main_keyword: e.target.value })} />
            ) },
            { title: '단일 MID', dataIndex: 'mid_single', width: 160, render: (v: any, r: any) => (
              <Input value={v as string} onChange={e => updateRow(r.key, { mid_single: e.target.value })} />
            ) },
            { title: '가격비교 MID', dataIndex: 'mid_compare', width: 180, render: (v: any, r: any) => (
              <Input value={v as string} onChange={e => updateRow(r.key, { mid_compare: e.target.value })} />
            ) },
            { title: '상품 URL', dataIndex: 'product_url', width: 260, render: (v: string, r: RowDraft) => (
              <Tooltip title={v && !isValidUrl(v) ? '올바른 URL 형식이 아닙니다 (http:// 또는 https:// 포함)' : ''}>
                <Input 
                  value={v} 
                  status={v && !isValidUrl(v) ? 'error' : undefined}
                  onChange={e => updateRow(r.key, { product_url: e.target.value })} 
                />
              </Tooltip>
            ) },
            { title: '가격비교 URL', dataIndex: 'compare_url', width: 260, render: (v: string, r: RowDraft) => (
              <Tooltip title={v && !isValidUrl(v) ? '올바른 URL 형식이 아닙니다 (http:// 또는 https:// 포함)' : ''}>
                <Input 
                  value={v} 
                  status={v && !isValidUrl(v) ? 'error' : undefined}
                  onChange={e => updateRow(r.key, { compare_url: e.target.value })} 
                />
              </Tooltip>
            ) },
            { title: '시작일', dataIndex: 'start_date', width: 150, render: (v: string | undefined, r: RowDraft) => {
              const invalid = !v || dayjs(v).startOf('day').isBefore(minDate);
              return (
                <Tooltip title={invalid ? (allowTodayStart ? '시작일은 오늘 이후여야 합니다.' : '시작일은 내일 이후여야 합니다.') : ''}>
                  <DatePicker
                    value={v ? dayjs(v as string) : undefined}
                    disabledDate={(current) => !!current && current.startOf('day').isBefore(minDate)}
                    status={invalid ? 'error' : undefined}
                    onChange={d => updateRow(r.key, { start_date: d ? d.format('YYYY-MM-DD') : '' })}
                  />
                </Tooltip>
              );
            } },
            { title: '종료일', dataIndex: 'end_date', width: 150, render: (v: string | undefined, r: RowDraft) => {
              const invalid = !v || (r.start_date ? dayjs(v).startOf('day').isBefore(dayjs(r.start_date).startOf('day')) : dayjs(v).startOf('day').isBefore(minDate));
              return (
                <Tooltip title={invalid ? '종료일은 시작일과 같거나 이후여야 합니다.' : ''}>
                  <DatePicker
                    value={v ? dayjs(v as string) : undefined}
                    disabledDate={(current) => {
                      if (!current) return false;
                      const c = current.startOf('day');
                      const min = r.start_date ? dayjs(r.start_date).startOf('day') : minDate;
                      return c.isBefore(min);
                    }}
                    status={invalid ? 'error' : undefined}
                    onChange={d => updateRow(r.key, { end_date: d ? d.format('YYYY-MM-DD') : '' })}
                  />
                </Tooltip>
              );
            } },
            { title: '메모', dataIndex: 'memo', width: 200, render: (v: any, r: any) => (
              <Input value={v as string} onChange={e => updateRow(r.key, { memo: e.target.value })} />
            ) },
            { title: '상태', dataIndex: 'status', render: (v: string | undefined) => (
              <Tag>{v || '정상'}</Tag>
            ) },
          ]}
          rowClassName={(r: RowDraft) => {
            const startInvalid = !r.start_date || dayjs(r.start_date).startOf('day').isBefore(minDate);
            return (!r.sellerId || startInvalid || !r.end_date) ? 'row-invalid' : '';
          }}
        />
      </FullWidth>
    </Modal>
  );
};

export default ExcelImportModal;


